import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DateRange } from "react-day-picker";
import { calculateFilterCount } from "src/features/fields/utils/utils";

export interface PoState {
    poDetails: any[] | null | undefined;
    poFilters: {
        company: any | any[] | undefined;
        division: any | any[] | undefined;
        counter: any | any[] | undefined;
        status: any | any[] | undefined;
        stockist: any | any[] | undefined;
        orders: any | any[] | undefined;
        products: any | any[] | undefined;
        additionalStatus: any | any[] | undefined
        state: any | any[] | undefined
        item_codes: any | any[] | undefined
        soType: any | any[] | undefined
        counters: any | any[] | undefined
        dateRange: DateRange | undefined;
    };
    stockistBlockFilters: {
        company: any | any[] | undefined;
        division: any | any[] | undefined;
        status: any | any[] | undefined;
        blockType: any | any[] | undefined;
        filterCount: number;
    },
    stockistRevokeFilters: {
        company: any | any[] | undefined;
        division: any | any[] | undefined;
    },
    orderTypeExceptionFilters: {
        company: any | any[] | undefined;
        division: any | any[] | undefined;
        state: any | any[] | undefined;
        cndnRemark: any | any[] | undefined;
        filterCount: number;
    },
    poFiltersData: {
        company: any | any[] | undefined;
        division: any | any[] | undefined;
        counter: any | any[] | undefined;
        status: any | any[] | undefined;
        stockist: any | any[] | undefined;
        orders: any | any[] | undefined;
        products: any | any[] | undefined;
        additionalStatus: any | any[] | undefined
        state: any | any[] | undefined
        item_codes: any | any[] | undefined
        soType: any | any[] | undefined
        counters: any | any[] | undefined
        dateRange: DateRange | undefined;
        stockistBlockDivision: any | any[] | undefined;
        stockistRevokeDivision: any | any[] | undefined;
        orderTypeExceptionDivision: any | any[] | undefined;
        cndnRemarks: any | any[] | undefined
    };
    stockistBlockTableData: any | null | undefined | any[];
    orderTypeExceptionTableData: any | null | undefined | any[];
    isResetFilter: boolean;
}

const poSlice = createSlice({
    name: "po",
    initialState: {
        poDetails: [] as any[] | null | undefined,
        poFilters: {
            company: undefined as any | any[] | undefined,
            division: undefined as any | any[] | undefined,
            counter: undefined as any | any[] | undefined,
            status: undefined as any | any[] | undefined,
            stockist: undefined as any | any[] | undefined,
            orders: undefined as any | any[] | undefined,
            products: undefined as any | any[] | undefined,
            additionalStatus: undefined as any | any[] | undefined,
            state: undefined as any | any[] | undefined,
            item_codes: undefined as any | any[] | undefined,
            soType: undefined as any | any[] | undefined,
            counters: undefined as any | any[] | undefined,
            dateRange: undefined as DateRange | undefined,
        },
        stockistBlockFilters: {
            company: undefined as any | any[] | undefined,
            division: undefined as any | any[] | undefined,
            status: [{ name: "Blocked", value: "blocked" }],
            blockType: undefined as any | any[] | undefined,
            filterCount: 0,
        },
        stockistRevokeFilters: {
            company: undefined as any | any[] | undefined,
            division: undefined as any | any[] | undefined,
        },
        orderTypeExceptionFilters: {
            company: undefined as any | any[] | undefined,
            division: undefined as any | any[] | undefined,
            state: undefined as any | any[] | undefined,
            cndnRemark: undefined as any | any[] | undefined,
            filterCount: 0,
        },
        poFiltersData: {
            company: undefined as any | any[] | undefined,
            division: undefined as any | any[] | undefined,
            counter: undefined as any | any[] | undefined,
            status: undefined as any | any[] | undefined,
            stockist: undefined as any | any[] | undefined,
            orders: undefined as any | any[] | undefined,
            products: undefined as any | any[] | undefined,
            additionalStatus: undefined as any | any[] | undefined,
            state: undefined as any | any[] | undefined,
            item_codes: undefined as any | any[] | undefined,
            soType: undefined as any | any[] | undefined,
            counters: undefined as any | any[] | undefined,
            dateRange: undefined as DateRange | undefined,
            stockistBlockDivision: undefined as DateRange | undefined,
            stockistRevokeDivision: undefined as DateRange | undefined,
            orderTypeExceptionDivision: undefined as DateRange | undefined,
            cndnRemark: undefined as DateRange | undefined,
        },
        stockistBlockTableData: [] as any[] | null | undefined,
        orderTypeExceptionTableData: [] as any[] | null | undefined,
        isResetFilter: false,
    },
    reducers: {
        setPoDetails(state, action: PayloadAction<any[] | null | undefined>): void {
            state.poDetails = action.payload;
        },
        setPoFilters(state, action: PayloadAction<{ key: keyof typeof state.poFilters; value: any | any[] | undefined }>) {
            const { key, value } = action.payload;
            if (key === "dateRange" && value) {
                state.poFilters[key] = value as DateRange; // Convert back to Date
            } else {
                state.poFilters[key] = value as any;
            }
        },
        setStockistBlockFilters(
            state,
            action: PayloadAction<{ key: keyof Omit<typeof state.stockistBlockFilters, "filterCount">; value: any | any[] | undefined }>
        ) {
            const { key, value } = action.payload;
            state.stockistBlockFilters[key] = value as any;

            const { filterCount, ...filtersWithoutCount } = state.stockistBlockFilters;
            state.stockistBlockFilters.filterCount = calculateFilterCount(filtersWithoutCount);
        },

        setStockistRevokeFilters(state, action: PayloadAction<{ key: keyof typeof state.stockistRevokeFilters; value: any | any[] | undefined }>) {
            const { key, value } = action.payload;
            state.stockistRevokeFilters[key] = value as any;
        },
        setOrderTypeExceptionFilters(
            state,
            action: PayloadAction<{ key: keyof Omit<typeof state.orderTypeExceptionFilters, "filterCount">; value: any | any[] | undefined }>
        ) {
            const { key, value } = action.payload;
            state.orderTypeExceptionFilters[key] = value as any;

            const { filterCount, ...filtersWithoutCount } = state.orderTypeExceptionFilters;
            state.orderTypeExceptionFilters.filterCount = calculateFilterCount(filtersWithoutCount);
        },
        setPoFiltersData(state, action: PayloadAction<{ key: keyof typeof state.poFiltersData; value: any | any[] | undefined }>) {
            const { key, value } = action.payload;
            state.poFiltersData[key] = value;
        },
        setStockistBlockTableData(state, action: PayloadAction<null | undefined>) {
            state.stockistBlockTableData = action.payload;
        },
        setOrderTypeExceptionTableData(state, action: PayloadAction<null | undefined>) {
            state.orderTypeExceptionTableData = action.payload;
        },
        setIsResetFilter(state, action: PayloadAction<boolean>) {
            state.isResetFilter = action.payload;
        },
    },
});

export const poActions = poSlice.actions;
export { poSlice };
